import { useEffect, useRef, useState } from "react";

import {
  playFog,
  type FogPattern,
  type FogPlayback,
} from "@nav-games/nav-lights";

interface FogPlayButtonProps {
  pattern: FogPattern;
  /// Optional label override — defaults to "▶ Play fog signal".
  label?: string;
}

/// Inline play/stop button for an IRPCS Rule 35 fog signal. Owns its
/// own AudioContext lifetime through `playFog`; cleanup on unmount
/// guarantees the tone stops when the user advances past a card or
/// closes the catalogue expander.
export function FogPlayButton({ pattern, label }: FogPlayButtonProps) {
  const handleRef = useRef<FogPlayback | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    return () => {
      handleRef.current?.stop();
      handleRef.current = null;
    };
  }, []);

  const start = () => {
    handleRef.current?.stop();
    setPlaying(true);
    const handle = playFog(pattern);
    handleRef.current = handle;
    void handle.done.then(() => {
      if (handleRef.current === handle) {
        handleRef.current = null;
        setPlaying(false);
      }
    });
  };

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setPlaying(false);
  };

  return (
    <button
      className="secondary"
      onClick={playing ? stop : start}
      style={{
        fontSize: 13,
        padding: "4px 10px",
        marginTop: 6,
      }}
    >
      {playing ? "■ Stop" : (label ?? "▶ Play fog signal")}
    </button>
  );
}
